/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * This is the ResponsiveTitle component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function ResponsiveTitle({ min, max, step, offset, children }) {
  const ref = useRef(null);
  const [size, setSize] = useState(null);
  const limit = max - min;

  useEffect(() => {
    function onResize() {
      setSize(null);
    }

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [size]);

  useEffect(() => {
    if (ref.current.offsetWidth + offset > window.innerWidth) {
      setSize(prev => {
        if (prev === null) {
          return 0;
        }

        if (prev < limit) {
          return prev + step;
        }

        return prev;
      });
    } else if (size === null) {
      setSize(0);
    }
  }, [size, step, limit]);

  function getStyle() {
    if (size === null) return null;

    return {
      fontSize: `${(max - size) / 16}em`,
    };
  }

  return (
    <h1 style={getStyle()}>
      <span ref={ref} dangerouslySetInnerHTML={{ __html: children }} />
    </h1>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
ResponsiveTitle.propTypes = {
  offset: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  children: PropTypes.string.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
ResponsiveTitle.defaultProps = {
  offset: 20,
  max: 176,
  min: 46,
  step: 10,
};

export default ResponsiveTitle;
